<template>
    <section
        :id="slice.primary.anchorName || ''"
        ref="featuresListRef"
        class="slice slice-features slice-features--list"
        aria-label="features"
        data-testid="slice-features-list"
        :data-mui-theme="slice.primary.theme"
    >
        <div class="slice-content">
            <div class="slice-features--list__panel mui-background-lighter">
                <PrismicRichText
                    v-if="isFilled.richText(slice.primary.title)"
                    :field="slice.primary.title"
                    class="mui-title slice-features--list__title"
                    data-testid="slice-features-list-title"
                />
                <div :class="['slice-features--list__content', {'slice-features--list__content--reverse': slice.primary.reverse}]">
                    <div class="slice-features--list__list" role="list" data-testid="slice-features-list-items">
                        <div class="slice-features--list__items-row">
                            <div class="slice-features--list__item-indicator">
                                <div class="slice-features--list__item-indicator-fill" :style="fillStyle" />
                            </div>
                            <div
                                ref="itemContainerRef"
                                class="slice-features--list__item-container"
                                data-testid="slice-features-list-item-container"
                                @scroll.passive="onMobileItemsScroll"
                            >
                                <div
                                    v-for="(item, index) in featureItems"
                                    :key="index"
                                    :ref="(el) => setItemRef(el, index)"
                                    role="listitem"
                                    class="slice-features--list__item"
                                    :class="{'slice-features--list__item--active': index === activeIndex}"
                                    :data-testid="`slice-features-list-item--${index}`"
                                >
                                    <h3
                                        v-if="item.title"
                                        class="slice-features--list__item-title"
                                        :data-testid="`slice-features-list-item-title--${index}`"
                                    >
                                        {{ item.title }}
                                    </h3>

                                    <PrismicRichText
                                        v-if="isFilled.richText(item.description)"
                                        :field="item.description"
                                        class="slice-features--list__item-description"
                                        :data-testid="`slice-features-list-item-description--${index}`"
                                    />
                                </div>
                            </div>
                        </div>

                        <MuiLink
                            v-if="link"
                            :href="link.url"
                            :text="link.text"
                            :target="link.target"
                            icon="chevron-right"
                            class="slice-features--list__link"
                            data-testid="slice-features-list-link"
                        />
                    </div>
                    <div class="slice-features--list__media" data-testid="slice-features-list-media">
                        <MuiImageWrapperWithShapes
                            enable-full-screen
                            :image1-url="activeImage1Url ?? ''"
                            :image2-url="activeImage2Url ?? ''"
                            :theme="slice.primary.theme"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, onBeforeUnmount, onMounted, type ComputedRef, type ComponentPublicInstance, ref} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {FeaturesSlice} from '@prismicio-types';
    import {MuiImageWrapperWithShapes, MuiLink} from '#components';
    import {useDisplayMobile} from '@malt/utils';
    import {useGsap} from '../../../composables/useGsap';
    import {getLink} from '../../../utils/linkHelper';

    type FeaturesListInZone = Extract<FeaturesSlice, {variation: 'featuresList'}>;
    type FeatureItem = NonNullable<FeaturesListInZone['primary']['items']>[number];
    type ScrollTriggerInstance = ReturnType<ReturnType<typeof useGsap>['ScrollTrigger']['create']>;

    const SCROLL_PX_PER_ITEM = 200;
    const HOLD_PX_PER_ITEM = 500;

    const props = defineProps(getSliceComponentProps<FeaturesListInZone>());

    const {isDisplayMobile} = useDisplayMobile('Xs');
    const {featureItems, link} = useFeatureItems();

    const featuresListRef = ref<HTMLElement | null>(null);
    const itemContainerRef = ref<HTMLElement | null>(null);
    const itemRefs = ref<HTMLElement[]>([]);

    function setItemRef(el: Element | ComponentPublicInstance | null, index: number) {
        if (el instanceof HTMLElement) {
            itemRefs.value[index] = el;
        }
    }

    function useFeatureItems() {
        const MAX_ITEMS = 4;
        const featureItems = computed(() => (props.slice.primary.items ?? []).slice(0, MAX_ITEMS));
        const link = computed(() => getLink(props.slice.primary.link ?? {}));

        return {featureItems, link};
    }

    const {activeIndex, fillStyle, activeImage1Url, activeImage2Url, onMobileItemsScroll, killScrollTrigger, setupScrollProgression} =
        useFeaturesListProgression(featureItems);

    function useFeaturesListProgression(featureItems: ComputedRef<FeatureItem[]>) {
        const {ScrollTrigger} = useGsap();

        const activeIndex = ref(0);
        const indicatorFillOffset = ref(0);
        const indicatorFillSize = ref(0);
        const remaining = ref(0);
        const continuousIndex = ref(0);
        const resolved = ref(false);

        const scrollTriggerInstance = ref<ScrollTriggerInstance | null>(null);

        const itemCount = computed(() => featureItems.value.length);

        const activeItem = computed(() => featureItems.value[activeIndex.value] ?? featureItems.value[0]);

        const activeImage1Url = computed(() => {
            const image = activeItem.value?.image1;
            return isFilled.image(image) ? image?.url : '';
        });

        const activeImage2Url = computed(() => {
            const image = activeItem.value?.image2;
            return isFilled.image(image) ? image?.url : '';
        });

        const fillStyle = computed(() => ({
            '--features-list-fill-offset': `${indicatorFillOffset.value * 100}%`,
            '--features-list-fill-size': `${indicatorFillSize.value * 100}%`,
        }));

        function totalScrollPx(count: number): number {
            return count * HOLD_PX_PER_ITEM + Math.max(0, count - 1) * SCROLL_PX_PER_ITEM;
        }

        // Maps scrub progress to a continuous item index with a hold, then a move, per item:
        // hold → transition → hold → transition → … → hold on last.
        function continuousIndexFromProgress(progress: number): number {
            const count = itemCount.value;
            if (count <= 1) {
                continuousIndex.value = 0;
                return continuousIndex.value;
            }

            remaining.value = Math.min(1, Math.max(0, progress)) * totalScrollPx(count);
            continuousIndex.value = count - 1;
            resolved.value = false;

            Array.from({length: count - 1}, (_, i) => i).forEach((i) => {
                if (resolved.value) {
                    return;
                }

                if (remaining.value <= HOLD_PX_PER_ITEM) {
                    continuousIndex.value = i;
                    resolved.value = true;
                    return;
                }
                remaining.value -= HOLD_PX_PER_ITEM;

                if (remaining.value <= SCROLL_PX_PER_ITEM) {
                    continuousIndex.value = i + remaining.value / SCROLL_PX_PER_ITEM;
                    resolved.value = true;
                    return;
                }
                remaining.value -= SCROLL_PX_PER_ITEM;
            });

            return continuousIndex.value;
        }

        function updateIndicatorFromContinuousIndex(itemProgress: number) {
            const count = itemCount.value;
            if (count <= 0) {
                indicatorFillOffset.value = 0;
                indicatorFillSize.value = 0;
                return;
            }

            const container = itemContainerRef.value;
            const items = itemRefs.value.slice(0, count).filter(Boolean);

            if (!container || items.length === 0) {
                const segment = 1 / count;
                indicatorFillOffset.value = itemProgress * segment;
                indicatorFillSize.value = segment;
                return;
            }

            const trackSize = container.clientHeight;
            if (trackSize <= 0) {
                return;
            }

            const containerTop = container.getBoundingClientRect().top;
            const currentItemIndex = Math.floor(itemProgress);
            const nextItemIndex = Math.min(items.length - 1, currentItemIndex + 1);
            const transitionProgress = itemProgress - currentItemIndex;

            if (!items[currentItemIndex] || !items[nextItemIndex]) {
                return;
            }

            const currentItemRect = items[currentItemIndex].getBoundingClientRect();
            const nextItemRect = items[nextItemIndex].getBoundingClientRect();
            const currentItemOffset = currentItemRect.top - containerTop;
            const nextItemOffset = nextItemRect.top - containerTop;

            const indicatorFillStart = currentItemOffset + (nextItemOffset - currentItemOffset) * transitionProgress;
            const indicatorFillHeight = currentItemRect.height + (nextItemRect.height - currentItemRect.height) * transitionProgress;

            indicatorFillOffset.value = indicatorFillStart / trackSize;
            indicatorFillSize.value = indicatorFillHeight / trackSize;
        }

        function selectFirstItem() {
            activeIndex.value = 0;
            updateIndicatorFromContinuousIndex(0);
        }

        function syncActiveIndexFromContinuousIndex(itemProgress: number) {
            activeIndex.value = Math.round(itemProgress);
        }

        function onMobileItemsScroll() {
            const el = itemContainerRef.value;
            const count = itemCount.value;
            if (!el || count <= 0) {
                return;
            }

            const maxScroll = el.scrollWidth - el.clientWidth;
            const thumbSize = el.scrollWidth > 0 ? el.clientWidth / el.scrollWidth : 1;

            indicatorFillSize.value = Math.min(1, thumbSize);
            indicatorFillOffset.value = maxScroll > 0 ? (el.scrollLeft / maxScroll) * (1 - indicatorFillSize.value) : 0;

            if (maxScroll <= 0) {
                activeIndex.value = 0;
                return;
            }

            const exactIndex = Math.min(1, Math.max(0, el.scrollLeft / maxScroll)) * (count - 1);
            activeIndex.value = Math.round(exactIndex);
        }

        function killScrollTrigger() {
            scrollTriggerInstance?.value?.kill();
            scrollTriggerInstance.value = null;
        }

        function setupScrollProgression() {
            killScrollTrigger();
            selectFirstItem();

            const count = itemCount.value;
            if (!featuresListRef.value || count <= 1) {
                return;
            }

            scrollTriggerInstance.value = ScrollTrigger.create({
                trigger: featuresListRef.value,
                start: 'center center',
                end: () => `+=${totalScrollPx(count)}`,
                pin: true,
                pinSpacing: true,
                scrub: 0.3,
                invalidateOnRefresh: true,
                onEnter: selectFirstItem,
                onUpdate: (self) => {
                    const continuousIndex = continuousIndexFromProgress(self.progress);
                    updateIndicatorFromContinuousIndex(continuousIndex);
                    syncActiveIndexFromContinuousIndex(continuousIndex);
                },
            });
        }

        return {
            activeIndex,
            fillStyle,
            activeImage1Url,
            activeImage2Url,
            onMobileItemsScroll,
            killScrollTrigger,
            setupScrollProgression,
        };
    }

    function syncProgressionToViewport() {
        if (isDisplayMobile.value) {
            killScrollTrigger();
            onMobileItemsScroll();
            return;
        }
        setupScrollProgression();
    }

    onMounted(() => {
        syncProgressionToViewport();
        window.addEventListener('resize', syncProgressionToViewport);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('resize', syncProgressionToViewport);
        killScrollTrigger();
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-features {
        &--list {
            .slice-content {
                text-align: left;
            }

            &__panel {
                border-radius: var(--joy-core-spacing-5);
                padding: var(--joy-core-spacing-6);

                @include mq.screen_md(min) {
                    padding: var(--joy-core-spacing-10);
                }
            }

            &__content {
                display: flex;
                gap: var(--joy-core-spacing-8);
                min-width: 0;
                align-items: center;

                &--reverse {
                    @include mq.screen_md(min) {
                        flex-direction: row-reverse;
                    }
                }
            }

            &__title {
                margin-bottom: var(--joy-core-spacing-8);
            }

            &__list {
                display: flex;
                flex-direction: column;
                gap: var(--joy-core-spacing-3);
                overflow: hidden;

                @include mq.screen_sm(min) {
                    overflow: visible;
                }
            }

            &__items-row {
                display: flex;
                flex-direction: column-reverse;
                align-items: stretch;
                gap: var(--joy-core-spacing-8);
                min-width: 0;
                padding-block: 2.5px;

                @include mq.screen_sm(min) {
                    flex-direction: row;
                    padding-block: 0;
                }
            }

            &__item-container {
                display: flex;
                flex: 1;
                flex-direction: row;
                gap: var(--joy-core-spacing-3);
                min-width: 0;
                overflow-x: auto;
                cursor: grab;
                scrollbar-width: none;

                &::-webkit-scrollbar {
                    display: none;
                }

                @include mq.screen_sm(min) {
                    flex-direction: column;
                    overflow-x: visible;
                    cursor: default;
                }
            }

            &__item {
                position: relative;
                padding: var(--joy-core-spacing-6);
                border-radius: var(--joy-core-radius-4);
                background-color: rgba(255, 255, 255, 0.6);
                min-width: 250px;
                transition: background-color var(--joy-transition-duration-default) var(--joy-transition-timing-function);

                @include mq.screen_md(min) {
                    min-width: auto;
                }

                &--active {
                    background-color: var(--joy-color-neutral-0);
                }
            }

            &__item-indicator {
                position: relative;
                z-index: 0;
                height: 12px;
                border-radius: 40px;
                flex-shrink: 0;
                background-color: var(--joy-color-neutral-0);

                @include mq.screen_sm(min) {
                    width: 12px;
                    height: auto;
                    align-self: stretch;
                    display: block;
                }
            }

            &__item-indicator-fill {
                position: absolute;
                z-index: 1;
                top: 50%;
                left: var(--features-list-fill-offset, 0%);
                width: var(--features-list-fill-size, 0%);
                height: 16px;
                transform: translateY(-50%);
                border-radius: 40px;
                background-color: var(--mui-color-background-darker);
                will-change: width, height, top, left;

                &--animated {
                    transition:
                        width var(--joy-transition-duration-default) var(--joy-transition-timing-function),
                        height var(--joy-transition-duration-default) var(--joy-transition-timing-function),
                        top var(--joy-transition-duration-default) var(--joy-transition-timing-function),
                        left var(--joy-transition-duration-default) var(--joy-transition-timing-function);
                }

                @include mq.screen_sm(min) {
                    top: var(--features-list-fill-offset, 0%);
                    left: 50%;
                    width: 21px;
                    height: var(--features-list-fill-size, 0%);
                    transform: translateX(-50%);
                }
            }

            &__item-title {
                @include text.getFontBySize(secondary-200);
                margin-bottom: var(--joy-core-spacing-2);
            }

            &__item-description {
                @include text.getFontBySize(primary-400);

                p {
                    margin: 0;
                }

                ul,
                ol {
                    margin: var(--joy-core-spacing-1) 0 0;
                    padding-left: var(--joy-core-spacing-5);
                    list-style-position: outside;
                }
            }

            &__link {
                align-self: flex-start;
                display: flex;
                flex-direction: row-reverse;
                margin-top: var(--joy-core-spacing-6);

                @include mq.screen_md(min) {
                    margin-left: 42px;
                }
            }

            &__media {
                display: none;

                @include mq.screen_md(min) {
                    display: block;
                    align-self: center;

                    .mui-c-wrapper-with-shapes {
                        aspect-ratio: 4/5;
                    }
                }

                @include mq.screen_lg(min) {
                    .mui-c-wrapper-with-shapes {
                        aspect-ratio: 1/1;
                    }
                }
            }
        }
    }
</style>
